import React, { useState, useEffect } from 'react';

interface Product {
  id: string;
  name: string;
  barcode: string;
  category: string;
  description: string;
  batchCount: number;
  totalProducts: number;
  lastBatch: string;
  status: 'active' | 'inactive';
  createdAt: string;
}

const ProductManagement: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const [newProduct, setNewProduct] = useState({
    name: '',
    barcode: '',
    category: '',
    description: ''
  });

  const categories = [
    'Alcohol & Beverages',
    'Food & Snacks',
    'Cosmetics & Personal Care',
    'Pharmaceuticals',
    'Electronics',
    'Textiles & Clothing',
    'Automotive Parts',
    'Home & Garden',
    'Other'
  ];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      // TODO: Replace with actual API call
      // Simulated data for now
      setTimeout(() => {
        setProducts([
          {
            id: '1',
            name: 'Coca-Cola 500ml',
            barcode: '1234567890123',
            category: 'Alcohol & Beverages',
            description: 'Classic Coca-Cola 500ml bottle',
            batchCount: 12,
            totalProducts: 15000,
            lastBatch: '2024-11-01',
            status: 'active',
            createdAt: '2024-10-15'
          },
          {
            id: '2',
            name: 'Sprite 350ml',
            barcode: '1234567890124',
            category: 'Alcohol & Beverages',
            description: 'Refreshing Sprite 350ml can',
            batchCount: 8,
            totalProducts: 9500,
            lastBatch: '2024-10-28',
            status: 'active',
            createdAt: '2024-10-10'
          },
          {
            id: '3',
            name: 'Fanta Orange 500ml',
            barcode: '1234567890125',
            category: 'Alcohol & Beverages',
            description: 'Orange flavored Fanta 500ml bottle',
            batchCount: 5,
            totalProducts: 6200,
            lastBatch: '2024-10-25',
            status: 'active',
            createdAt: '2024-10-05'
          }
        ]);
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      setLoading(false);
    }
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // TODO: Replace with actual API call
      const response = await fetch('/api/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newProduct)
      });

      if (response.ok) {
        // Refresh products list
        await fetchProducts();
        setShowAddForm(false);
        setNewProduct({ name: '', barcode: '', category: '', description: '' });
        alert('Product added successfully!');
      } else {
        alert('Failed to add product. Please try again.');
      }
    } catch (error) {
      alert('Failed to add product. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.barcode.includes(searchTerm);
    const matchesCategory = selectedCategory === '' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading && products.length === 0) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        padding: '40px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '20px',
          textAlign: 'center',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#64748b', margin: 0 }}>Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      padding: '40px 20px'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: 'white',
          padding: '30px 40px',
          borderRadius: '20px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          marginBottom: '30px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '20px'
          }}>
            <div>
              <h1 style={{
                fontSize: '2rem',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '8px'
              }}>
                Product Management
              </h1>
              <p style={{
                color: '#64748b',
                fontSize: '16px',
                margin: 0
              }}>
                Manage your products and view batch statistics
              </p>
            </div>
            
            <button
              onClick={() => setShowAddForm(true)}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '12px 20px',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'transform 0.3s ease'
              }}
            >
              + Add Product
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{
          backgroundColor: 'white',
          padding: '20px 30px',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          marginBottom: '30px'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px',
            alignItems: 'end'
          }}>
            <div>
              <label style={{
                display: 'block',
                color: '#374151',
                fontWeight: '600',
                marginBottom: '8px',
                fontSize: '14px'
              }}>
                Search Products
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or barcode..."
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                color: '#374151',
                fontWeight: '600',
                marginBottom: '8px',
                fontSize: '14px'
              }}>
                Filter by Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: 'white'
                }}
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                }}
                style={{
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  padding: '10px 20px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}>
          {filteredProducts.map((product) => (
            <div
              key={product.id}
              style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '16px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e2e8f0',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px -5px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
              }}
            >
              {/* Product Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '16px'
              }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    color: '#1e293b',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    marginBottom: '4px'
                  }}>
                    {product.name}
                  </h3>
                  <p style={{
                    color: '#64748b',
                    fontSize: '12px',
                    margin: '0 0 8px 0'
                  }}>
                    Barcode: {product.barcode}
                  </p>
                  <span style={{
                    backgroundColor: '#eff6ff',
                    color: '#1e40af',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600'
                  }}>
                    {product.category}
                  </span>
                </div>
                
                <div style={{
                  backgroundColor: product.status === 'active' ? '#f0fdf4' : '#fef2f2',
                  color: product.status === 'active' ? '#166534' : '#991b1b',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  {product.status}
                </div>
              </div>

              {/* Product Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  backgroundColor: '#f8fafc',
                  padding: '12px',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <p style={{
                    color: '#1e293b',
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    margin: '0 0 4px 0'
                  }}>
                    {product.batchCount}
                  </p>
                  <p style={{
                    color: '#64748b',
                    fontSize: '11px',
                    margin: 0,
                    textTransform: 'uppercase',
                    fontWeight: '600'
                  }}>
                    Batches
                  </p>
                </div>

                <div style={{
                  backgroundColor: '#f8fafc',
                  padding: '12px',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <p style={{
                    color: '#1e293b',
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    margin: '0 0 4px 0'
                  }}>
                    {product.totalProducts.toLocaleString()}
                  </p>
                  <p style={{
                    color: '#64748b',
                    fontSize: '11px',
                    margin: 0,
                    textTransform: 'uppercase',
                    fontWeight: '600'
                  }}>
                    Products
                  </p>
                </div>

                <div style={{
                  backgroundColor: '#f8fafc',
                  padding: '12px',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <p style={{
                    color: '#1e293b',
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    margin: '0 0 4px 0'
                  }}>
                    KSh {product.totalProducts.toLocaleString()}
                  </p>
                  <p style={{
                    color: '#64748b',
                    fontSize: '11px',
                    margin: 0,
                    textTransform: 'uppercase',
                    fontWeight: '600'
                  }}>
                    Revenue
                  </p>
                </div>
              </div>

              {/* Product Description */}
              <p style={{
                color: '#64748b',
                fontSize: '14px',
                lineHeight: '1.5',
                marginBottom: '16px'
              }}>
                {product.description}
              </p>

              {/* Last Batch Info */}
              <div style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <p style={{
                  color: '#92400e',
                  fontSize: '12px',
                  fontWeight: '600',
                  margin: '0 0 4px 0'
                }}>
                  Last Batch Upload:
                </p>
                <p style={{
                  color: '#92400e',
                  fontSize: '14px',
                  margin: 0
                }}>
                  {new Date(product.lastBatch).toLocaleDateString()}
                </p>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '8px'
              }}>
                <a
                  href={`/manufacturer/batches?product=${product.id}`}
                  style={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: '8px 16px',
                    textDecoration: 'none',
                    borderRadius: '6px',
                    fontWeight: '600',
                    fontSize: '12px',
                    textAlign: 'center',
                    transition: 'transform 0.3s ease'
                  }}
                >
                  Add Batch
                </a>
                <button
                  style={{
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                    padding: '8px 16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                                        fontWeight: '600',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => alert('Edit functionality coming soon!')}
                >
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredProducts.length === 0 && !loading && (
          <div style={{
            backgroundColor: 'white',
            padding: '60px 40px',
            borderRadius: '20px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '20px' }}>📦</div>
            <h3 style={{
              color: '#1e293b',
              fontSize: '1.3rem',
              fontWeight: '600',
              marginBottom: '12px'
            }}>
              {searchTerm || selectedCategory ? 'No products found' : 'No products yet'}
            </h3>
            <p style={{
              color: '#64748b',
              fontSize: '16px',
              marginBottom: '24px'
            }}>
              {searchTerm || selectedCategory 
                ? 'Try adjusting your search or filter criteria'
                : 'Add your first product to start protecting your brand with ScanOnce'
              }
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '12px 24px',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '16px',
                cursor: 'pointer',
                transition: 'transform 0.3s ease'
              }}
            >
              Add Your First Product
            </button>
          </div>
        )}

        {/* Add Product Modal */}
        {showAddForm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '40px',
              borderRadius: '20px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '30px'
              }}>
                <h2 style={{
                  color: '#1e293b',
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  margin: 0
                }}>
                  Add New Product
                </h2>
                <button
                  onClick={() => setShowAddForm(false)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#64748b'
                  }}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleAddProduct}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    color: '#374151',
                    fontWeight: '600',
                    marginBottom: '8px'
                  }}>
                    Product Name *
                  </label>
                  <input
                    type="text"
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    placeholder="e.g., Coca-Cola 500ml"
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    color: '#374151',
                    fontWeight: '600',
                    marginBottom: '8px'
                  }}>
                    Barcode *
                  </label>
                  <input
                    type="text"
                    value={newProduct.barcode}
                    onChange={(e) => setNewProduct({...newProduct, barcode: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    placeholder="e.g., 1234567890123"
                  />
                  <p style={{
                    color: '#64748b',
                    fontSize: '12px',
                    margin: '4px 0 0 0'
                  }}>
                    Enter the barcode printed on your product packaging
                  </p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    color: '#374151',
                    fontWeight: '600',
                    marginBottom: '8px'
                  }}>
                    Category *
                  </label>
                  <select
                    value={newProduct.category}
                    onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '16px',
                      outline: 'none',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '30px' }}>
                  <label style={{
                    display: 'block',
                    color: '#374151',
                    fontWeight: '600',
                    marginBottom: '8px'
                  }}>
                    Description
                  </label>
                  <textarea
                    value={newProduct.description}
                    onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '16px',
                      outline: 'none',
                      resize: 'vertical'
                    }}
                    placeholder="Brief description of the product..."
                  />
                </div>

                <div style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    style={{
                      backgroundColor: '#f1f5f9',
                      color: '#475569',
                      padding: '12px 24px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      background: loading ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      padding: '12px 24px',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    {loading ? 'Adding...' : 'Add Product'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductManagement;

